import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost } from '../../api';
import { DASHBOARD_HOST, DASHBOARD_URL } from '../../lib/dashboardUrl';
import { toast } from 'react-toastify';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../../components/Pagination';
import {
  User, Network, TrendingUp, FileText, BarChart2, Settings, ArrowLeft, X,
} from 'lucide-react';

// ─── Shared Helpers ──────────────────────────────────────────────────────────

function getDefaultDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function StatCard({ label, value }) {
  const formatted = label.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
  const display = value === null || value === undefined
    ? '—'
    : typeof value === 'object'
      ? JSON.stringify(value)
      : String(value);
  return (
    <div className="rounded-lg border border-border/70 bg-card p-5">
      <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1 capitalize">
        {formatted}
      </p>
      <p className="font-mono text-xl text-foreground break-words">{display}</p>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-sm font-medium uppercase tracking-[0.06em] text-muted-foreground mb-4 pb-2 border-b border-border/40">
      {children}
    </h2>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-ginox-green" />
    </div>
  );
}

function TableSpinner({ cols }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green mx-auto" />
      </td>
    </tr>
  );
}

function EmptyRow({ cols, message = 'No data found' }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted-foreground">
        {message}
      </td>
    </tr>
  );
}

const TH = ({ children }) => (
  <th className="border-b border-border/70 px-5 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-[#9aa1ac] whitespace-nowrap">
    {children}
  </th>
);

const TD = ({ children, mono }) => (
  <td className={`border-b border-border/40 px-5 py-3.5 text-sm text-foreground ${mono ? 'font-mono' : ''}`}>
    {children}
  </td>
);

const inputClass = "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
const labelClass = "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

// ─── Overview Section ─────────────────────────────────────────────────────────

function OverviewSection({ username }) {
  const [stats, setStats] = useState(null);
  const [creditStats, setCreditStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [statsRes, creditRes] = await Promise.all([
          apiGet(`/api/admin/user/${username}/stats`, {}),
          apiGet(`/api/admin/user/${username}/app-credit-stats`, {}),
        ]);
        if (statsRes.data.success) setStats(statsRes.data.data);
        if (creditRes.data.success) setCreditStats(creditRes.data.data);
      } catch {
        toast.error('Failed to load overview');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [username]);

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      {stats && (
        <div>
          <SectionTitle>User Stats</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {Object.entries(stats).map(([key, value]) => (
              <StatCard key={key} label={key} value={value} />
            ))}
          </div>
        </div>
      )}
      {creditStats && (
        <div>
          <SectionTitle>App Credit Stats</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {Object.entries(creditStats).map(([key, value]) => (
              <StatCard key={key} label={key} value={value} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Referral Section ─────────────────────────────────────────────────────────

function ReferralSection({ username }) {
  const [creditStats, setCreditStats] = useState(null);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [networkStats, setNetworkStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [creditRes, subRes, netRes] = await Promise.all([
          apiGet(`/api/admin/user/${username}/referral-credit-stats`, {}),
          apiGet(`/api/admin/user/${username}/referral-subscription-stats`, {}),
          apiGet(`/api/admin/user/${username}/referral-network-stats`, {}),
        ]);
        if (creditRes.data.success) setCreditStats(creditRes.data.data);
        if (subRes.data.success) setSubscriptionStats(subRes.data.data);
        if (netRes.data.success) setNetworkStats(netRes.data.data);
      } catch {
        toast.error('Failed to load referral data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [username]);

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      {creditStats && (
        <div>
          <SectionTitle>Referral Credit Stats</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {Object.entries(creditStats).map(([key, value]) => (
              <StatCard key={key} label={key} value={value} />
            ))}
          </div>
        </div>
      )}
      {subscriptionStats && (
        <div>
          <SectionTitle>Referral Subscription Stats</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {Object.entries(subscriptionStats).map(([key, value]) => (
              <StatCard key={key} label={key} value={value} />
            ))}
          </div>
        </div>
      )}
      {networkStats && (
        <div>
          <SectionTitle>Referral Network Stats</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
            {Object.entries(networkStats)
              .filter(([, v]) => v === null || typeof v !== 'object')
              .map(([key, value]) => (
                <StatCard key={key} label={key} value={value} />
              ))}
          </div>
          {networkStats.productTotals && Object.keys(networkStats.productTotals).length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">Product Totals</p>
              <div className="overflow-x-auto">
                <table className="w-full border-separate border-spacing-0">
                  <thead>
                    <tr>
                      {['Product', 'Total Income', 'Total Sale'].map(c => <TH key={c}>{c}</TH>)}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(networkStats.productTotals).map(([product, data]) => (
                      <tr key={product} className="group hover:bg-white/[0.02] transition-colors">
                        <TD>{product}</TD>
                        <TD mono>{data.totalIncome}</TD>
                        <TD mono>{data.totalSale}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {networkStats.isElite && networkStats.eliteIncome && (
            <div className="mb-4">
              <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">Elite Income</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                <StatCard label="Total Earnings" value={networkStats.eliteIncome.totalEarnings} />
              </div>
            </div>
          )}
          {Array.isArray(networkStats.levels) && networkStats.levels.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">Levels</p>
              <div className="overflow-x-auto">
                <table className="w-full border-separate border-spacing-0">
                  <thead>
                    <tr>
                      {['Level', 'Total Users', 'Earnings'].map(c => <TH key={c}>{c}</TH>)}
                    </tr>
                  </thead>
                  <tbody>
                    {networkStats.levels.map(lvl => (
                      <tr key={lvl.level} className="group hover:bg-white/[0.02] transition-colors">
                        <TD>{lvl.level}</TD>
                        <TD>{lvl.totalUsers}</TD>
                        <TD mono>{lvl.earnings}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Income Section ───────────────────────────────────────────────────────────

function IncomeSection({ username }) {
  const [income, setIncome] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({ ...getDefaultDates(), status: 'all', order: 'desc' });
  const [useDates, setUseDates] = useState(false);

  const fetchIncome = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      let query = `/api/admin/user/${username}/income?page=${pageNum}&pageSize=15`;
      if (filters.status && filters.status !== 'all') query += `&status=${filters.status}`;
      if (filters.order) query += `&order=${filters.order}`;
      if (useDates) query += `&startDate=${filters.startDate}&endDate=${filters.endDate}`;
      const res = await apiGet(query, {});
      setIncome(res.data.data.data);
      setTotalPages(res.data.data.totalPages);
      setTotalRows(res.data.data.totalRows || 0);
    } catch {
      toast.error('Failed to fetch income history');
    } finally {
      setLoading(false);
    }
  }, [username, filters, useDates]);

  useEffect(() => { fetchIncome(page); }, [page, fetchIncome]);

  const handleChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setPage(1);
  };

  return (
    <div>
      <SectionTitle>Income History</SectionTitle>
      <div className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className={labelClass}>Status</label>
            <select name="status" value={filters.status} onChange={handleChange} className={inputClass} style={{ width: 'auto', minWidth: '120px' }}>
              <option value="all">All</option>
              <option value="released">Released</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Order</label>
            <select name="order" value={filters.order} onChange={handleChange} className={inputClass} style={{ width: 'auto', minWidth: '140px' }}>
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
          <div className="flex items-center gap-2 pb-0.5">
            <input type="checkbox" id="incomeUseDates" checked={useDates}
              onChange={(e) => { setUseDates(e.target.checked); setPage(1); }}
              className="h-4 w-4 rounded border-border/70 accent-ginox-green" />
            <label htmlFor="incomeUseDates" className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground cursor-pointer">
              Filter by Date
            </label>
          </div>
          {useDates && (
            <>
              <div>
                <label className={labelClass}>Start Date</label>
                <input type="date" name="startDate" value={filters.startDate} onChange={handleChange}
                  max={filters.endDate} className={inputClass} style={{ width: 'auto' }} />
              </div>
              <div>
                <label className={labelClass}>End Date</label>
                <input type="date" name="endDate" value={filters.endDate} onChange={handleChange}
                  min={filters.startDate} max={new Date().toISOString().slice(0, 10)}
                  className={inputClass} style={{ width: 'auto' }} />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>{['Tranx ID', 'Amount', 'Wallet', 'Type', 'Released', 'Date'].map(c => <TH key={c}>{c}</TH>)}</tr>
          </thead>
          <tbody>
            {loading ? <TableSpinner cols={6} /> : income.length === 0 ? <EmptyRow cols={6} message="No income found" /> :
              income.map(i => (
                <tr key={i._id} className="group hover:bg-white/[0.02] transition-colors">
                  <TD mono>{i.tranx_id}</TD>
                  <TD>{i.amount}</TD>
                  <TD>{i.wallet_type}</TD>
                  <TD>{i.type}</TD>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${i.is_released ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`}>
                      {i.is_released ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <TD mono>{new Date(i.createdAt).toLocaleString()}</TD>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination currentPage={page} totalPages={totalPages} totalRows={totalRows} pageSize={15} onPageChange={setPage} />
      </div>
    </div>
  );
}

// ─── Statement Section ────────────────────────────────────────────────────────

function StatementSection({ username }) {
  const [statement, setStatement] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({ ...getDefaultDates(), order: 'desc', wallet_type: 'GUSD' });
  const [useDates, setUseDates] = useState(false);

  const fetchStatement = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      let query = `/api/admin/user/${username}/statement?page=${pageNum}&pageSize=15`;
      if (filters.order) query += `&order=${filters.order}`;
      query += `&wallet_type=${filters.wallet_type}`;
      if (useDates) query += `&startDate=${filters.startDate}&endDate=${filters.endDate}`;
      const res = await apiGet(query, {});
      setStatement(res.data.data.data);
      setTotalPages(res.data.data.totalPages);
      setTotalRows(res.data.data.totalRows || 0);
    } catch {
      toast.error('Failed to fetch statement');
    } finally {
      setLoading(false);
    }
  }, [username, filters, useDates]);

  useEffect(() => { fetchStatement(page); }, [page, fetchStatement]);

  const handleChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setPage(1);
  };

  return (
    <div>
      <SectionTitle>Wallet Statement</SectionTitle>
      <div className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className={labelClass}>Order</label>
            <select name="order" value={filters.order} onChange={handleChange} className={inputClass} style={{ width: 'auto', minWidth: '140px' }}>
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Wallet Type</label>
            <select name="wallet_type" value={filters.wallet_type} onChange={handleChange} className={inputClass} style={{ width: 'auto', minWidth: '140px' }}>
              <option value="GUSD">GUSD</option>
              <option value="BFM">BFM</option>
              <option value="APP_CREDIT">APP_CREDIT</option>
              <option value="FOREX">FOREX</option>
            </select>
          </div>
          <div className="flex items-center gap-2 pb-0.5">
            <input type="checkbox" id="stmtUseDates" checked={useDates}
              onChange={(e) => { setUseDates(e.target.checked); setPage(1); }}
              className="h-4 w-4 rounded border-border/70 accent-ginox-green" />
            <label htmlFor="stmtUseDates" className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground cursor-pointer">
              Filter by Date
            </label>
          </div>
          {useDates && (
            <>
              <div>
                <label className={labelClass}>Start Date</label>
                <input type="date" name="startDate" value={filters.startDate} onChange={handleChange}
                  max={filters.endDate} className={inputClass} style={{ width: 'auto' }} />
              </div>
              <div>
                <label className={labelClass}>End Date</label>
                <input type="date" name="endDate" value={filters.endDate} onChange={handleChange}
                  min={filters.startDate} max={new Date().toISOString().slice(0, 10)}
                  className={inputClass} style={{ width: 'auto' }} />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>{['Tranx ID', 'Type', 'Wallet', 'Amount', 'Total', 'Message', 'Date'].map(c => <TH key={c}>{c}</TH>)}</tr>
          </thead>
          <tbody>
            {loading ? <TableSpinner cols={7} /> : statement.length === 0 ? <EmptyRow cols={7} message="No statement found" /> :
              statement.map(s => (
                <tr key={s._id} className="group hover:bg-white/[0.02] transition-colors">
                  <TD mono>{s.tranx_id}</TD>
                  <TD>{s.tranx_type}</TD>
                  <TD>{s.wallet_type}</TD>
                  <TD mono>{s.amount}</TD>
                  <TD mono>{s.total}</TD>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground max-w-xs truncate">{s.message}</td>
                  <TD mono>{new Date(s.createdAt).toLocaleString()}</TD>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination currentPage={page} totalPages={totalPages} totalRows={totalRows} pageSize={15} onPageChange={setPage} />
      </div>
    </div>
  );
}

// ─── Reports Section ──────────────────────────────────────────────────────────

function ReportsSection({ username }) {
  const [subTab, setSubTab] = useState('sponsor');

  const [sponsorData, setSponsorData] = useState([]);
  const [sponsorLoading, setSponsorLoading] = useState(false);
  const [sponsorPage, setSponsorPage] = useState(1);
  const [sponsorTotalPages, setSponsorTotalPages] = useState(1);
  const [sponsorTotalRows, setSponsorTotalRows] = useState(0);

  const [gusdData, setGusdData] = useState([]);
  const [gusdLoading, setGusdLoading] = useState(false);
  const [gusdPage, setGusdPage] = useState(1);
  const [gusdTotalPages, setGusdTotalPages] = useState(1);
  const [gusdTotalRows, setGusdTotalRows] = useState(0);

  const fetchSponsor = useCallback(async (pageNum) => {
    setSponsorLoading(true);
    try {
      const res = await apiGet(`/api/admin/reports/income/sponsor_bonus?username=${username}&page=${pageNum}&pageSize=15`, {});
      if (res.data.success) {
        setSponsorData(res.data.data.data);
        setSponsorTotalPages(res.data.data.totalPages);
        setSponsorTotalRows(res.data.data.totalRows || 0);
      }
    } catch { toast.error('Error fetching sponsor bonus report'); }
    finally { setSponsorLoading(false); }
  }, [username]);

  const fetchGusd = useCallback(async (pageNum) => {
    setGusdLoading(true);
    try {
      const res = await apiGet(`/api/admin/reports/statement/GUSD?username=${username}&page=${pageNum}&pageSize=15`, {});
      if (res.data.success) {
        setGusdData(res.data.data.data);
        setGusdTotalPages(res.data.data.totalPages);
        setGusdTotalRows(res.data.data.totalRows || 0);
      }
    } catch { toast.error('Error fetching GUSD wallet statement'); }
    finally { setGusdLoading(false); }
  }, [username]);

  useEffect(() => { if (subTab === 'sponsor') fetchSponsor(sponsorPage); }, [subTab, sponsorPage, fetchSponsor]);
  useEffect(() => { if (subTab === 'gusd') fetchGusd(gusdPage); }, [subTab, gusdPage, fetchGusd]);

  return (
    <div>
      <SectionTitle>Reports</SectionTitle>
      <div className="flex gap-2 mb-5">
        {[
          { id: 'sponsor', label: 'Sponsor Bonus Income' },
          { id: 'gusd', label: 'GUSD Wallet Statement' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition border ${
              subTab === t.id
                ? 'border-ginox-green/60 bg-ginox-green/10 text-ginox-green'
                : 'border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'sponsor' && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>{['Tranx ID', 'Amount', 'Wallet', 'Type', 'Released', 'Date'].map(c => <TH key={c}>{c}</TH>)}</tr>
              </thead>
              <tbody>
                {sponsorLoading ? <TableSpinner cols={6} /> : sponsorData.length === 0 ? <EmptyRow cols={6} /> :
                  sponsorData.map(item => (
                    <tr key={item._id} className="group hover:bg-white/[0.02] transition-colors">
                      <TD mono>{item.tranx_id}</TD>
                      <TD>{item.amount}</TD>
                      <TD>{item.wallet_type}</TD>
                      <TD>{item.type}</TD>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${item.is_released ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`}>
                          {item.is_released ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <TD mono>{new Date(item.createdAt).toLocaleString()}</TD>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
          <div className="border-t border-white/[0.08] px-4">
            <Pagination currentPage={sponsorPage} totalPages={sponsorTotalPages} totalRows={sponsorTotalRows} pageSize={15} onPageChange={setSponsorPage} />
          </div>
        </>
      )}

      {subTab === 'gusd' && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>{['Tranx ID', 'Type', 'Wallet', 'Amount', 'Total', 'Message', 'Date'].map(c => <TH key={c}>{c}</TH>)}</tr>
              </thead>
              <tbody>
                {gusdLoading ? <TableSpinner cols={7} /> : gusdData.length === 0 ? <EmptyRow cols={7} /> :
                  gusdData.map(item => (
                    <tr key={item._id} className="group hover:bg-white/[0.02] transition-colors">
                      <TD mono>{item.tranx_id}</TD>
                      <TD>{item.tranx_type}</TD>
                      <TD>{item.wallet_type}</TD>
                      <TD mono>{item.amount}</TD>
                      <TD mono>{item.total}</TD>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground max-w-xs truncate">{item.message}</td>
                      <TD mono>{new Date(item.createdAt).toLocaleString()}</TD>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
          <div className="border-t border-white/[0.08] px-4">
            <Pagination currentPage={gusdPage} totalPages={gusdTotalPages} totalRows={gusdTotalRows} pageSize={15} onPageChange={setGusdPage} />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Actions Section ──────────────────────────────────────────────────────────

function ActionCard({ title, description, buttonLabel, variant = 'default', onClick, disabled }) {
  const btnClass = {
    default: 'rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50',
    danger:  'rounded-md border border-red-500/30 bg-red-500/10 px-4 py-2 text-xs font-medium text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50',
    purple:  'rounded-md border border-border/70 bg-white/[0.03] px-4 py-2 text-xs font-medium text-muted-foreground hover:bg-white/[0.06] hover:text-foreground transition-colors disabled:opacity-50',
    orange:  'rounded-md border border-border/70 bg-white/[0.03] px-4 py-2 text-xs font-medium text-muted-foreground hover:bg-white/[0.06] hover:text-foreground transition-colors disabled:opacity-50',
  }[variant] || '';

  return (
    <div className="rounded-lg border border-border/70 bg-card p-5 flex flex-col">
      <h3 className="text-sm font-medium text-foreground mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground mb-4 flex-1">{description}</p>
      <button onClick={onClick} disabled={disabled} className={btnClass}>
        {buttonLabel}
      </button>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="rounded-xl border border-border/70 bg-card p-6 shadow-xl w-full max-w-md relative">
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          onClick={onClose}
        >
          <X className="w-4 h-4" />
        </button>
        <h3 className="text-sm font-medium uppercase tracking-[0.06em] text-foreground mb-5">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function ActionsSection({ username, user }) {
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [withdrawStatus, setWithdrawStatus] = useState(null);
  const [loadingWithdraw, setLoadingWithdraw] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawComment, setWithdrawComment] = useState('');

  const [showConvertBD, setShowConvertBD] = useState(false);
  const [convertBDForm, setConvertBDForm] = useState({ sponsor_id: '', updateSponsor: '1' });
  const [loadingConvert, setLoadingConvert] = useState(false);

  const [showLoginConfirm, setShowLoginConfirm] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);

  const [showResetEpin, setShowResetEpin] = useState(false);
  const [newEpin, setNewEpin] = useState('');
  const [loadingResetEpin, setLoadingResetEpin] = useState(false);

  const [showReset2FA, setShowReset2FA] = useState(false);
  const [loadingReset2FA, setLoadingReset2FA] = useState(false);

  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [loadingUpdateStatus, setLoadingUpdateStatus] = useState(false);

  const fetchWithdrawStatus = async () => {
    const res = await apiGet(`/api/admin/user/get-withdraw-status?username=${username}`, {});
    if (res.data.success) setWithdrawStatus(res.data.data);
    else toast.error(res.data.message || 'Failed to fetch withdraw status');
  };

  const handleViewWithdraw = async () => {
    setLoadingWithdraw(true);
    setShowWithdraw(true);
    try { await fetchWithdrawStatus(); }
    catch { toast.error('Error fetching withdraw status'); }
    finally { setLoadingWithdraw(false); }
  };

  // Toggle, not a setter: the server flips whatever is stored. Re-read after so changedBy/changedAt are fresh.
  const handleToggleWithdraw = async () => {
    setLoadingWithdraw(true);
    try {
      const res = await apiPost('/api/admin/user/update-withdraw-status', { username, comment: withdrawComment.trim() });
      if (res.data.success) {
        toast.success('Withdraw status updated');
        setWithdrawComment('');
        await fetchWithdrawStatus();
      }
      else toast.error(res.data.message || 'Failed to update withdraw status');
    } catch (err) { toast.error(err.response?.data?.message || 'Error updating withdraw status'); }
    finally { setLoadingWithdraw(false); }
  };

  const handleConvertBD = async (e) => {
    e.preventDefault();
    setLoadingConvert(true);
    try {
      const res = await apiPost('/api/admin/user/convert/to-bd', {
        username, sponsor_id: convertBDForm.sponsor_id, updateSponsor: convertBDForm.updateSponsor,
      });
      if (res.data.success) { toast.success('User converted to BD'); setShowConvertBD(false); }
      else toast.error(res.data.message || 'Failed to convert to BD');
    } catch { toast.error('Error converting to BD'); }
    finally { setLoadingConvert(false); }
  };

  const handleConvertNU = async () => {
    setLoadingConvert(true);
    try {
      const res = await apiPost('/api/admin/user/convert/to-nu', { username });
      if (res.data.success) toast.success('User converted to NU');
      else toast.error(res.data.message || 'Failed to convert to NU');
    } catch { toast.error('Error converting to NU'); }
    finally { setLoadingConvert(false); }
  };

  const handleLoginAsUser = async () => {
    setLoadingLogin(true);
    let newWindow = window.open('about:blank', '_blank');
    try {
      const res = await apiPost(`/api/admin/user/${username}/token`, {});
      if (res.data.success) {
        const { name, surname, email, role } = res.data.data;
        const token = res.data.token;
        const redirectUrl = `${DASHBOARD_URL}/admin-signin?username=${encodeURIComponent(username)}&name=${encodeURIComponent(name + ' ' + surname)}&email=${encodeURIComponent(email)}&role=${encodeURIComponent(role)}&token=${encodeURIComponent(token)}`;
        newWindow.location.href = redirectUrl;
        toast.success('Redirecting to user dashboard...');
        setShowLoginConfirm(false);
      } else {
        newWindow?.close();
        toast.error(res.data.message || 'Failed to generate user token');
      }
    } catch (err) {
      newWindow?.close();
      toast.error(err.message || 'Error generating user token');
    } finally { setLoadingLogin(false); }
  };

  const handleResetEpin = async () => {
    setLoadingResetEpin(true);
    try {
      const res = await apiPost('/api/admin/user/reset-epin', { username });
      if (res.data.success) { setNewEpin(res.data.ePin); toast.success('E-PIN reset successfully'); }
      else toast.error(res.data.message || 'Failed to reset E-PIN');
    } catch { toast.error('Error resetting E-PIN'); }
    finally { setLoadingResetEpin(false); }
  };

  const handleUpdateStatus = async () => {
    setLoadingUpdateStatus(true);
    try {
      const res = await apiPost('/api/admin/user/update-status', { username });
      if (res.data.success) { toast.success(`Status updated${res.data.status ? ` to ${res.data.status}` : ''}`); setShowUpdateStatus(false); }
      else toast.error(res.data.message || 'Failed to update status');
    } catch (err) { toast.error(err.response?.data?.message || 'Error updating status'); }
    finally { setLoadingUpdateStatus(false); }
  };

  const handleReset2FA = async () => {
    setLoadingReset2FA(true);
    try {
      const res = await apiPost('/api/admin/user/reset-2fa', { username });
      if (res.data.success) { toast.success('2FA reset successfully'); setShowReset2FA(false); }
      else toast.error(res.data.message || 'Failed to reset 2FA');
    } catch { toast.error('Error resetting 2FA'); }
    finally { setLoadingReset2FA(false); }
  };

  return (
    <div>
      <SectionTitle>Admin Actions</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {can('POST:/api/admin/user/:username/edit') && (
          <ActionCard
            title="Edit User"
            description="Update the user's name, surname, or email address."
            buttonLabel="Edit User"
            onClick={() => navigate(`/users/edit/${username}?name=${encodeURIComponent(user?.name || '')}&surname=${encodeURIComponent(user?.surname || '')}&email=${encodeURIComponent(user?.email || '')}`)}
          />
        )}
        {can('POST:/api/admin/user/reset-password') && (
          <ActionCard
            title="Reset Password"
            description="Trigger a password reset for this user's account."
            buttonLabel="Reset Password"
            onClick={() => navigate(`/users/update-password/${username}`)}
          />
        )}
        {can('POST:/api/admin/user/update-status') && (
          <ActionCard
            title="Update Account Status"
            description="Activate or deactivate this user's account."
            buttonLabel="Update Status"
            onClick={() => setShowUpdateStatus(true)}
          />
        )}
        {can('GET:/api/admin/user/get-withdraw-status') && (
          <ActionCard
            title="Withdraw Status"
            description="View and toggle this user's withdrawal capability."
            buttonLabel="View Withdraw Status"
            onClick={handleViewWithdraw}
          />
        )}
        {can('POST:/api/admin/user/convert/to-bd') && user?.role === 'NU' && (
          <ActionCard
            title="Convert to BD"
            description="Upgrade this user from Normal User to Business Development."
            buttonLabel="Convert to BD"
            variant="purple"
            onClick={() => setShowConvertBD(true)}
            disabled={loadingConvert}
          />
        )}
        {can('POST:/api/admin/user/convert/to-nu') && user?.role === 'BD' && (
          <ActionCard
            title="Convert to NU"
            description="Downgrade this user from Business Development to Normal User."
            buttonLabel="Convert to NU"
            variant="orange"
            onClick={handleConvertNU}
            disabled={loadingConvert}
          />
        )}
        {can('POST:/api/admin/user/reset-epin') && (
          <ActionCard
            title="Reset E-PIN"
            description="Generate a new E-PIN. The current E-PIN will be immediately invalidated."
            buttonLabel="Reset E-PIN"
            variant="danger"
            onClick={() => { setNewEpin(''); setShowResetEpin(true); }}
          />
        )}
        {can('POST:/api/admin/user/reset-2fa') && (
          <ActionCard
            title="Reset 2FA"
            description="Disable two-factor authentication. The user will need to re-enroll."
            buttonLabel="Reset 2FA"
            variant="danger"
            onClick={() => setShowReset2FA(true)}
          />
        )}
        {can('POST:/api/admin/user/:username/token') && (
          <ActionCard
            title="Login as User"
            description={`Open a new tab signed in as this user on ${DASHBOARD_HOST}.`}
            buttonLabel="Login as User"
            onClick={() => setShowLoginConfirm(true)}
          />
        )}
      </div>

      {/* Withdraw Status Modal */}
      {showWithdraw && (
        <Modal title="Withdraw Status" onClose={() => setShowWithdraw(false)}>
          {loadingWithdraw && withdrawStatus === null
            ? <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green" /></div>
            : (
              <div className="mb-5 space-y-3">
                <div>
                  <p className={labelClass}>Withdraw Disabled</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${withdrawStatus?.withdraw_disabled ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
                    {withdrawStatus?.withdraw_disabled ? 'Yes' : 'No'}
                  </span>
                </div>
                {withdrawStatus?.comment && (
                  <div>
                    <p className={labelClass}>Reason</p>
                    <p className="text-sm text-foreground whitespace-pre-wrap break-words">{withdrawStatus.comment}</p>
                  </div>
                )}
                {withdrawStatus?.changedBy && (
                  <div>
                    <p className={labelClass}>Changed By</p>
                    <p className="text-sm text-foreground">{withdrawStatus.changedBy}</p>
                  </div>
                )}
                {withdrawStatus?.changedAt && (
                  <div>
                    <p className={labelClass}>Changed At</p>
                    <p className="text-sm font-mono text-foreground">{new Date(withdrawStatus.changedAt).toLocaleString()}</p>
                  </div>
                )}
              </div>
            )
          }
          {can('POST:/api/admin/user/update-withdraw-status') && withdrawStatus && (
            <div className="space-y-3">
              <div>
                <textarea value={withdrawComment} onChange={(e) => setWithdrawComment(e.target.value)} maxLength={500} rows={3}
                  placeholder={withdrawStatus.withdraw_disabled ? 'Reason for re-enabling (optional)' : 'Why are withdrawals being disabled?'}
                  className={inputClass} />
                <p className="text-right text-xs text-muted-foreground">{withdrawComment.length}/500</p>
              </div>
              <button onClick={handleToggleWithdraw} disabled={loadingWithdraw}
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50">
                {loadingWithdraw ? 'Updating...' : withdrawStatus.withdraw_disabled ? 'Enable Withdrawals' : 'Disable Withdrawals'}
              </button>
            </div>
          )}
        </Modal>
      )}

      {/* Convert to BD Modal */}
      {showConvertBD && (
        <Modal title="Convert to BD" onClose={() => setShowConvertBD(false)}>
          <form onSubmit={handleConvertBD} className="space-y-4">
            <div>
              <label className={labelClass}>Sponsor ID</label>
              <input value={convertBDForm.sponsor_id}
                onChange={e => setConvertBDForm(f => ({ ...f, sponsor_id: e.target.value }))}
                placeholder="Sponsor ID"
                className={inputClass}
                required />
            </div>
            <div>
              <label className={labelClass}>Update Sponsor?</label>
              <select value={convertBDForm.updateSponsor}
                onChange={e => setConvertBDForm(f => ({ ...f, updateSponsor: e.target.value }))}
                className={inputClass}>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button type="button" onClick={() => setShowConvertBD(false)}
                className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={loadingConvert}
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50">
                {loadingConvert ? 'Converting...' : 'Convert'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Login Confirm Modal */}
      {showLoginConfirm && (
        <Modal title="Login as User" onClose={() => setShowLoginConfirm(false)}>
          <p className="text-sm text-muted-foreground mb-6">
            You will be redirected to <span className="font-mono text-foreground">{DASHBOARD_HOST}/admin-signin</span> in a new tab with this user's credentials.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowLoginConfirm(false)}
              className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
              Cancel
            </button>
            <button onClick={handleLoginAsUser} disabled={loadingLogin}
              className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50">
              {loadingLogin ? 'Processing...' : 'Confirm & Redirect'}
            </button>
          </div>
        </Modal>
      )}

      {/* Reset E-PIN Modal */}
      {showResetEpin && (
        <Modal title="Reset E-PIN" onClose={() => { setShowResetEpin(false); setNewEpin(''); }}>
          {newEpin ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">E-PIN reset successfully for <span className="font-mono text-foreground">{username}</span>.</p>
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">New E-PIN</p>
                <p className="text-2xl font-bold font-mono text-emerald-400">{newEpin}</p>
              </div>
              <p className="text-xs text-muted-foreground">Share this E-PIN with the user through a secure channel.</p>
              <button onClick={() => { setShowResetEpin(false); setNewEpin(''); }}
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors">
                Close
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Reset E-PIN for <span className="font-mono text-foreground">{username}</span>?</p>
              <p className="text-xs text-red-400">The current E-PIN will be immediately invalidated.</p>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowResetEpin(false)}
                  className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
                  Cancel
                </button>
                <button onClick={handleResetEpin} disabled={loadingResetEpin}
                  className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-2 text-xs font-medium text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50">
                  {loadingResetEpin ? 'Resetting...' : 'Reset E-PIN'}
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Update Status Modal */}
      {showUpdateStatus && (
        <Modal title="Update Account Status" onClose={() => setShowUpdateStatus(false)}>
          <p className="text-sm text-muted-foreground mb-1">Toggle the account status for <span className="font-mono text-foreground">{username}</span>?</p>
          <p className="text-xs text-muted-foreground mb-6">An active account will be deactivated, and an inactive one reactivated.</p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowUpdateStatus(false)}
              className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
              Cancel
            </button>
            <button onClick={handleUpdateStatus} disabled={loadingUpdateStatus}
              className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50">
              {loadingUpdateStatus ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </Modal>
      )}

      {/* Reset 2FA Modal */}
      {showReset2FA && (
        <Modal title="Reset 2FA" onClose={() => setShowReset2FA(false)}>
          <p className="text-sm text-muted-foreground mb-1">Reset 2FA for <span className="font-mono text-foreground">{username}</span>?</p>
          <p className="text-xs text-red-400 mb-6">This will disable two-factor authentication. The user will need to re-enroll.</p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowReset2FA(false)}
              className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
              Cancel
            </button>
            <button onClick={handleReset2FA} disabled={loadingReset2FA}
              className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-2 text-xs font-medium text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50">
              {loadingReset2FA ? 'Resetting...' : 'Reset 2FA'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Tab Config ───────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',   label: 'Overview',   icon: <User className="w-4 h-4" /> },
  { id: 'referral',   label: 'Referral',   icon: <Network className="w-4 h-4" /> },
  { id: 'income',     label: 'Income',     icon: <TrendingUp className="w-4 h-4" /> },
  { id: 'statement',  label: 'Statement',  icon: <FileText className="w-4 h-4" /> },
  { id: 'reports',    label: 'Reports',    icon: <BarChart2 className="w-4 h-4" /> },
  { id: 'actions',    label: 'Actions',    icon: <Settings className="w-4 h-4" /> },
];

// ─── Main Component ───────────────────────────────────────────────────────────

const UserProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') || 'overview';
  const user = location.state?.user;

  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/users')}
          className="rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors flex items-center gap-1.5 shrink-0"
        >
          <ArrowLeft className="w-3 h-3" />
          Back
        </button>
        <div>
          <h1 className="font-serif italic text-2xl sm:text-3xl text-foreground leading-tight">User Profile</h1>
          {user && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {user.name} {user.surname} &bull; <span className="font-mono">{user.email}</span>
            </p>
          )}
        </div>
      </div>

      {/* Mobile Tab Bar */}
      <div className="md:hidden mb-4 rounded-lg border border-border/70 bg-card overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/40">
          <div className="w-9 h-9 rounded-full bg-ginox-green/10 border border-ginox-green/30 flex items-center justify-center text-ginox-green font-bold text-sm shrink-0 select-none">
            {username?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-mono text-sm text-foreground">#{username}</div>
            {user?.name && (
              <div className="text-xs text-muted-foreground truncate">{user.name} {user.surname}</div>
            )}
          </div>
          <div className="flex gap-1.5 ml-auto shrink-0">
            {user?.role && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border border-border/70 bg-white/[0.03] text-muted-foreground">
                {user.role}
              </span>
            )}
            {user?.accountStatus && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${user.accountStatus === 'Active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                {user.accountStatus}
              </span>
            )}
          </div>
        </div>
        <nav className="flex overflow-x-auto scrollbar-none px-2 py-2 gap-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap shrink-0 transition border ${
                activeTab === tab.id
                  ? 'border-ginox-green/60 bg-ginox-green/10 text-ginox-green'
                  : 'border-transparent text-muted-foreground hover:bg-white/[0.05] hover:text-foreground'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        {/* Left Sub-Sidebar (desktop only) */}
        <aside className="hidden md:block w-52 shrink-0">
          <div className="rounded-lg border border-border/70 bg-card sticky top-4 overflow-hidden">
            {/* Identity Card */}
            <div className="p-4 border-b border-border/40">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-ginox-green/10 border border-ginox-green/30 flex items-center justify-center text-ginox-green font-bold text-base shrink-0 select-none">
                  {username?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-mono text-sm text-foreground truncate">
                    #{username}
                  </div>
                  {user?.name && (
                    <div className="text-xs text-muted-foreground truncate">
                      {user.name} {user.surname}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {user?.role && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border border-border/70 bg-white/[0.03] text-muted-foreground">
                    {user.role}
                  </span>
                )}
                {user?.accountStatus && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${user.accountStatus === 'Active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                    {user.accountStatus}
                  </span>
                )}
              </div>
            </div>

            {/* Nav */}
            <nav className="p-2">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-xs font-medium transition mb-0.5 border ${
                    activeTab === tab.id
                      ? 'border-ginox-green/60 bg-ginox-green/10 text-ginox-green'
                      : 'border-transparent text-muted-foreground hover:bg-white/[0.05] hover:text-foreground'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Right Content */}
        <main className="flex-1 min-w-0 w-full">
          {activeTab === 'overview'  && <OverviewSection  username={username} />}
          {activeTab === 'referral'  && <ReferralSection  username={username} />}
          {activeTab === 'income'    && <IncomeSection    username={username} />}
          {activeTab === 'statement' && <StatementSection username={username} />}
          {activeTab === 'reports'   && <ReportsSection   username={username} />}
          {activeTab === 'actions'   && <ActionsSection   username={username} user={user} />}
        </main>
      </div>
    </div>
  );
};

export default UserProfile;
